import { Button, ButtonColorEnum, ChevronDownIcon, ChevronUpIcon } from '@acceso/design-system';
import clsx from 'clsx';
import { useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

import Listbox, { ListboxSelected } from '@/components/Listbox';
import { TableRow } from '@/components/TableRow';
import { ReliabilityInlineBadge } from '@/components/VideoPresencesInlineControls/ReliabilityInlineBadge';
import { RELIABILITY } from '@/constants/reliability';
import { useBroadcastScheduleContext } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/context/BroadcastScheduleProvider';
import { buildNewPresenceDataToSave } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/helpers/batchSelectionHelpers';
import {
  INLINE_DIALOG_OPTIONS,
  PostPresenceFor,
  TBroadcastScheduleOnUpdateInline,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { TPresenceItem } from '@/types/presences';

type ReliabilityInlineDropdownProps<T extends TPresenceItem> = {
  item: T;
  onUpdateInline?: TBroadcastScheduleOnUpdateInline<PostPresenceFor<T>>;
  onPreview?: (item: T) => void;
  buildDataToSave?: (item: T) => PostPresenceFor<T>;
  presenceData?: { id?: string };
};

export function ReliabilityInlineDropdown<T extends TPresenceItem>({
  item,
  onUpdateInline,
  onPreview,
  buildDataToSave,
  presenceData,
}: ReliabilityInlineDropdownProps<T>) {
  const { logoData, playerVideoCurrentTime } = useBroadcastScheduleProgram();
  const { openInline, toggleInline, dropdownCoords, setDropdownCoordsFromRect } =
    useBroadcastScheduleContext();

  const activeData = presenceData ?? logoData;
  const dataToSave = (buildDataToSave ?? buildNewPresenceDataToSave) as (
    item: T
  ) => PostPresenceFor<T>;

  const {
    id: itemId,
    entity_score: entityScore,
    parameters: { is_manual: isManual },
  } = item as any;
  const isOpen = openInline.reliability === itemId;
  const btnRef = useRef<HTMLButtonElement | null>(null);

  useLayoutEffect(() => {
    if (isOpen && btnRef.current) {
      if (activeData?.id !== item.id) {
        onPreview?.(item);
      }
      setDropdownCoordsFromRect(btnRef.current.getBoundingClientRect());
    }
  }, [isOpen]);

  if (!itemId) return <></>;
  const entityScoreParsed = entityScore.toFixed(2) || 0;
  const updateReliability = (value: ListboxSelected) => {
    const reliabilitySelected = parseFloat(value[0]) as number;
    const newData = dataToSave(item);
    const applySilently = false;

    (newData as any).entity_score = reliabilitySelected;
    (newData.parameters as any).is_manual = true;

    onUpdateInline?.(newData, applySilently, playerVideoCurrentTime);
    toggleInline(INLINE_DIALOG_OPTIONS.Reliability, itemId);
  };

  return (
    <div className="relative">
      <Button
        ref={btnRef}
        color={ButtonColorEnum.SECONDARY_OLD}
        className={clsx(
          'h-auto cursor-pointer',
          openInline.reliability === itemId ? 'bg-neutral-20' : 'bg-transparent'
        )}
        onClick={() => {
          toggleInline(INLINE_DIALOG_OPTIONS.Reliability, itemId);
        }}
        rightIcon={() =>
          openInline.reliability === itemId ? <ChevronUpIcon /> : <ChevronDownIcon />
        }
      >
        <div className="flex flex-col items-center gap-0.5">
          <TableRow content={entityScoreParsed} className="flex flex-col" />
          <ReliabilityInlineBadge
            className="w-[73px] justify-center text-xs"
            isManual={isManual || false}
          />
        </div>
      </Button>
      {isOpen &&
        dropdownCoords &&
        createPortal(
          <div
            className="absolute z-100 mt-2 min-w-[300px] border border-neutral-20 bg-white p-4 shadow-lg"
            style={{
              top: dropdownCoords.top,
              left: dropdownCoords.right - 300, // container width
            }}
          >
            <Listbox
              dark={false}
              mode="single"
              items={RELIABILITY}
              onChange={updateReliability}
              identifiers={{
                key: 'value',
                value: 'value',
                label: 'label',
              }}
              selected={undefined}
            />
          </div>,
          document.body
        )}
    </div>
  );
}
